import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import ExternalReviewPackageOverview from '../components/externalReview/ExternalReviewPackageOverview';
import ControlledDemoBanner from '../components/externalReview/ControlledDemoBanner';
import ResponsibleUseBoundary from '../components/externalReview/ResponsibleUseBoundary';
import WorkplaceConditionObservedSection from '../components/safetyReports/WorkplaceConditionObservedSection';
import ClosureSummaryArtifactSection from '../components/safetyReports/ClosureSummaryArtifactSection';
import { BuyerReportSection } from './BuyerReportSection';
import { DecisionReadinessSection } from './DecisionReadinessSection';
import ExternalEvidenceSummarySection from '../components/externalReview/ExternalEvidenceSummarySection';
import ExternalRepresentativeEvidenceSection from '../components/externalReview/ExternalRepresentativeEvidenceSection';
import { resolveExternalOutcomeContext } from '../domain/externalReview/resolveExternalOutcomeContext';
import { sanitizeExternalLabel } from '../domain/externalReview/externalReportLanguage';
import { toExternalSafetyReviewModel } from '../domain/externalReview/toExternalSafetyReviewModel';
import { resolveInvestigationAlignmentFromPolicyContext } from '../domain/reportContext/investigationAlignment';
import { resolveClaimsRoiFromPolicyContext } from '../domain/reportContext/claimsRoiAlignment';
import { resolveSafetyControlReviewFromPolicyContext, resolveSafetyControlReviewFromReviewPayload } from '../domain/reportContext/safetyControlReview';
import InvestigationAlignmentCard from '../components/safetyReports/InvestigationAlignmentCard';
import ClaimsRoiAlignmentCard from '../components/safetyReports/ClaimsRoiAlignmentCard';
import SafetyControlReviewCard from '../components/safetyReports/SafetyControlReviewCard';
import type { DecisionReadinessPayload, SafetyScanBuyerReportPayload, SafetyScanReportV2Payload } from '../services/reportsApi';
import { reportsApi } from '../services/reportsApi';

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export default function ExternalSafetyReviewPage() {
  const { reportId } = useParams<{ reportId: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [pdfDownloadLoading, setPdfDownloadLoading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [model, setModel] = useState<ReturnType<typeof toExternalSafetyReviewModel>>(null);

  useEffect(() => {
    const id = String(reportId || '').trim();
    if (!id) {
      setError('Missing Safety Scan report id.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await reportsApi.getExternalSafetyReviewArtifact(id);
        if (cancelled) return;
        const mapped = toExternalSafetyReviewModel(response as Record<string, unknown>);
        if (!mapped) {
          setError('Unable to load this Signed Safety Scan for external review.');
          return;
        }
        setModel(mapped);
      } catch (err: unknown) {
        if (cancelled) return;
        setError('This Signed Safety Scan could not be loaded for external review.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [reportId]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !model) {
    return <Alert severity="error">{error || 'Safety Scan not available.'}</Alert>;
  }

  const outcomeContext = resolveExternalOutcomeContext(model.isDemoOrLoopedSource);
  const buyerReport = (model.buyerReport || null) as SafetyScanBuyerReportPayload | null;
  const decisionReadiness = (model.decisionReadiness || null) as DecisionReadinessPayload | null;
  const safetyScanReportV2 = (model.safetyScanReportV2 || null) as SafetyScanReportV2Payload | null;
  const investigationAlignment = resolveInvestigationAlignmentFromPolicyContext(
    buyerReport?.policy_report_context || null,
  );
  const claimsRoiAlignment = resolveClaimsRoiFromPolicyContext(
    buyerReport?.policy_report_context || null,
  );
  const safetyControlReview = resolveSafetyControlReviewFromPolicyContext(
    buyerReport?.policy_report_context || null,
  ) || resolveSafetyControlReviewFromReviewPayload(buyerReport?.safety_control_review);
  const conditionLabel = sanitizeExternalLabel(model.workplaceConditionObserved.conditionLabel, {
    isDemo: model.isDemoOrLoopedSource,
  });
  const areaLabel = sanitizeExternalLabel(model.workplaceConditionObserved.areaLabel, {
    isDemo: model.isDemoOrLoopedSource,
  });

  const handleDownloadPackage = async () => {
    const id = String(reportId || '').trim();
    if (!id) return;
    setDownloadLoading(true);
    setDownloadError(null);
    try {
      const { blob, filename } = await reportsApi.downloadExternalSafetyScanPackage(id);
      downloadBlob(blob, filename);
    } catch {
      setDownloadError('Unable to download the Signed Safety Scan package.');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    const id = String(reportId || '').trim();
    if (!id) return;
    setPdfDownloadLoading(true);
    setDownloadError(null);
    try {
      const { blob, filename } = await reportsApi.downloadExternalSafetyScanPdf(id);
      downloadBlob(blob, filename);
    } catch {
      setDownloadError('Unable to download the Signed Safety Scan PDF.');
    } finally {
      setPdfDownloadLoading(false);
    }
  };

  return (
    <Stack spacing={2.5}>
      {model.isDemoOrLoopedSource ? (
        <ControlledDemoBanner text={model.controlledDemoBannerText || undefined} />
      ) : null}
      <ResponsibleUseBoundary text={model.responsibleUseBoundaryText || undefined} />

      <ExternalReviewPackageOverview model={model} />

      <InvestigationAlignmentCard
        alignment={investigationAlignment}
        compact
        title="Investigation alignment"
        testId="external-review-investigation-alignment"
      />
      <ClaimsRoiAlignmentCard
        alignment={claimsRoiAlignment}
        compact
        title="Claims / ROI alignment"
        testId="external-review-claims-roi"
      />
      <SafetyControlReviewCard
        alignment={safetyControlReview}
        compact
        title="Safety control review"
        testId="external-review-safety-control-review"
      />

      <Stack spacing={1}>
        <Typography variant="h5" fontWeight={700}>
          {model.title}
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip size="small" label={model.statusLabel} color="success" variant="outlined" />
          {model.isDemoOrLoopedSource ? (
            <Chip size="small" label="Controlled demo feed" variant="outlined" />
          ) : null}
          {model.scanWindow.from || model.scanWindow.to ? (
            <Chip
              size="small"
              label={`${model.scanWindow.from || '—'} to ${model.scanWindow.to || '—'}`}
              variant="outlined"
            />
          ) : null}
        </Stack>
      </Stack>

      {model.workplaceConditionObserved.workplaceCondition ? (
        <WorkplaceConditionObservedSection
          workplaceCondition={model.workplaceConditionObserved.workplaceCondition as never}
        />
      ) : (
        <Card variant="outlined" data-testid="external-workplace-condition">
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Workplace condition observed
            </Typography>
            <Typography variant="body1">{conditionLabel}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Area: {areaLabel}
            </Typography>
            {model.scanWindow.from || model.scanWindow.to ? (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Scan window: {model.scanWindow.from || '—'} to {model.scanWindow.to || '—'}
              </Typography>
            ) : null}
          </CardContent>
        </Card>
      )}

      <ExternalEvidenceSummarySection
        evidenceSummary={model.evidenceSummary}
        isDemoOrLoopedSource={model.isDemoOrLoopedSource}
      />

      <ExternalRepresentativeEvidenceSection
        representativeEvidence={
          (model.representativeEvidence as Record<string, unknown> | null) || null
        }
        isDemoOrLoopedSource={model.isDemoOrLoopedSource}
        alwaysShow
      />

      <BuyerReportSection
        externalSafe
        data={buyerReport}
        outcomeContext={outcomeContext}
        safetyScanReportV2={safetyScanReportV2}
      />

      <ClosureSummaryArtifactSection
        externalSafe
        isDemoOrLoopedSource={model.isDemoOrLoopedSource}
        closureSummary={model.closureSummary as never}
      />

      <DecisionReadinessSection
        externalSafe
        decisionReadiness={decisionReadiness}
        outcomeContext={outcomeContext}
        impactDenominatorTrusted={false}
        impactMeasurementWaived={model.isDemoOrLoopedSource}
        showWhatChangedRates={false}
        showWhatChangedAuditRates={false}
        whatChangedSummaryText={
          model.isDemoOrLoopedSource
            ? 'Exposure reduction is not claimed from this report'
            : 'Follow-up measurement status recorded'
        }
        impactBaselineRatePerHour={null}
        impactFollowupRatePerHour={null}
        decisionMissingInputsDisplay={[]}
        decisionWarnings={[]}
      />

      <Box data-print-hide="true">
        {downloadError ? (
          <Alert severity="error" sx={{ mb: 1 }}>
            {downloadError}
          </Alert>
        ) : null}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Download the server-generated external-safe PDF or full package for your review record.
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <Button
            variant="contained"
            onClick={handleDownloadPdf}
            disabled={pdfDownloadLoading || downloadLoading}
            data-testid="external-download-pdf"
          >
            {pdfDownloadLoading ? 'Preparing PDF…' : 'Download Signed Safety Scan PDF'}
          </Button>
          <Button
            variant="outlined"
            onClick={handleDownloadPackage}
            disabled={downloadLoading || pdfDownloadLoading}
            data-testid="external-download-package"
          >
            {downloadLoading ? 'Preparing package…' : 'Download full package (ZIP)'}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
}
